export type RefLike<T> = { current: T };

export function propertyIdMatches(a: any, b: any) {
  return a != null && b != null && String(a) === String(b);
}

export function buildLoanIdOptions(loanIds: any[]) {
  if (!loanIds?.length) return [];
  return loanIds.map((item: any, index: number) => {
    if (typeof item === "object" && item?.primaryLoanId != null) {
      return {
        ...item,
        label: `${item.primaryLoanId} / ${item.toorakLoanId ?? ""} / ${item.originatorName ?? ""}`,
        index
      };
    }
    return {
      primaryLoanId: item,
      toorakLoanId: item,
      originatorName: "",
      label: String(item),
      value: item,
      index
    };
  });
}

export function resolveLoanIdsFromOption(
  selectedOption: any,
  fallbackToorakLoanId: any
) {
  if (!selectedOption) {
    return { primaryLoanId: null, toorakLoanId: null };
  }
  if (typeof selectedOption === "object") {
    return {
      primaryLoanId: selectedOption.primaryLoanId,
      toorakLoanId: selectedOption.toorakLoanId
    };
  }
  return {
    primaryLoanId: selectedOption,
    toorakLoanId: fallbackToorakLoanId ?? selectedOption
  };
}

export function filterLoanIdOptions(options: any[], inputValue: string) {
  if (!inputValue?.trim()) return options;
  const search = inputValue.trim().toLowerCase();
  return options.filter((opt: any) => {
    const primary =
      opt?.primaryLoanId != null ? String(opt.primaryLoanId).toLowerCase() : "";
    const toorak =
      opt?.toorakLoanId != null ? String(opt.toorakLoanId).toLowerCase() : "";
    const name =
      opt?.originatorName != null ? String(opt.originatorName).toLowerCase() : "";
    return (
      primary.includes(search) || toorak.includes(search) || name.includes(search)
    );
  });
}

export function getLoanIdOptionValue(
  availableLoanIds: any[],
  selectedLoanId: string | null,
  toorakLoanId: any
) {
  return (
    availableLoanIds.find(
      (opt: any) =>
        opt?.primaryLoanId === selectedLoanId ||
        opt?.toorakLoanId === toorakLoanId
    ) ?? null
  );
}

export function isLoanIdOptionEqualToValue(option: any, value: any) {
  return (
    option?.primaryLoanId === value?.primaryLoanId ||
    option?.primaryLoanId === value
  );
}

export function resolvePropertyOption(
  value: any,
  propertyDetailsOptions: any[],
  selectedProperty: any[]
) {
  if (value == null || value === "") return null;
  const index = Number(value);
  if (
    typeof index === "number" &&
    !Number.isNaN(index) &&
    propertyDetailsOptions?.[index] != null
  ) {
    return propertyDetailsOptions[index];
  }
  return (
    propertyDetailsOptions?.find((opt: any) =>
      propertyIdMatches(opt?.propertyId, value)
    ) ??
    selectedProperty.find((property: any) =>
      propertyIdMatches(property?.propertyId, value)
    ) ??
    null
  );
}

export function getSelectedPropertySelectValue(
  selectedProperty: any[],
  propertyDetailsOptions: any[]
) {
  const selected = selectedProperty?.[0];
  if (!selected || !propertyDetailsOptions?.length) return "";
  const byRef = propertyDetailsOptions.indexOf(selected);
  if (byRef >= 0) return String(byRef);
  const byId =
    selected?.propertyId != null
      ? propertyDetailsOptions.findIndex((opt: any) =>
          propertyIdMatches(opt?.propertyId, selected?.propertyId)
        )
      : -1;
  if (byId >= 0) return String(byId);
  const byAddress = propertyDetailsOptions.findIndex(
    (opt: any) => opt?.propertyAddress === selected?.propertyAddress
  );
  return byAddress >= 0 ? String(byAddress) : "";
}

export function getPropertyAddresses(selectedProperty: any[]) {
  return selectedProperty.length && typeof selectedProperty[0] !== "string"
    ? selectedProperty.map((item: any) => item?.propertyAddress)
    : selectedProperty;
}

export function getSyncedPropertySelection(drawDetails: any) {
  return drawDetails.propertyDetails?.length
    ? [drawDetails.propertyDetails[0]]
    : [];
}

export function getLoanIdOptionLabel(option: any) {
  return option?.primaryLoanId != null ? String(option.primaryLoanId) : "";
}

export function getLoanIdRenderLabel(option: any) {
  return (
    option?.label ??
    `${option?.primaryLoanId ?? ""} / ${option?.toorakLoanId ?? ""} / ${option?.originatorName ?? ""}`.trim()
  );
}

export function getPropertyRenderAddress(
  value: any,
  propertyDetailsOptions: any[],
  selectedProperty: any[]
) {
  if (value == null || value === "") return null;
  const idx = Number(value);
  const obj =
    !Number.isNaN(idx) && propertyDetailsOptions?.[idx] != null
      ? propertyDetailsOptions[idx]
      : selectedProperty?.[0];
  return obj?.propertyAddress ?? "";
}

export function buildDrawAutosaveFingerprint(
  primaryLoanId: any,
  drawAmountDetails: any,
  propertyDetails: any,
  bankDetails: any
) {
  return JSON.stringify({
    primaryLoanId,
    drawAmountDetails,
    propertyDetails,
    bankDetails
  });
}

export const PUT_COOLDOWN_MS = 600;
export const AUTOSAVE_DEBOUNCE_MS = 1100;

export function getAutosaveDelay(elapsedSincePut: number) {
  const inCooldown = elapsedSincePut < PUT_COOLDOWN_MS;
  return inCooldown
    ? Math.max(AUTOSAVE_DEBOUNCE_MS, PUT_COOLDOWN_MS - elapsedSincePut)
    : AUTOSAVE_DEBOUNCE_MS;
}

export function shouldSkipDraftAutosaveSchedule(params: {
  stage: any;
  draft: string;
  isOriginatorViewDraw: boolean;
  lastPutFingerprint: string;
  currentFingerprint: string;
}): "skip" | "prime" | "schedule" {
  if (params.stage !== params.draft || params.isOriginatorViewDraw) {
    return "skip";
  }
  if (params.lastPutFingerprint === "") return "prime";
  if (params.currentFingerprint === params.lastPutFingerprint) return "skip";
  return "schedule";
}
